import {useForm, Controller} from 'react-hook-form';
import {useMutation} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {useCallback, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Loader2} from 'lucide-react';
import dayjs from 'dayjs';
import {API} from '@chekin/api';
import {InputController as Input} from '@guestapp/old-ui-kit';
import ChekinProSettings from 'ChekinProSettings';
import {usePath} from 'hooks';
import {useChekinSDK} from 'context/ChekinSDK';
import {PATHS} from 'routes/constants';
import Button from 'components/common/Button';
import Datepicker from 'components/common/Datepicker';
import {IncompleteDataModal} from './IncompleteDataModal';
import {NotFoundReservationModal} from './NotFoundReservationModal';
import {FormTypes, NEXT_REQUIRED_FIELDS} from './types';
import {extractErrorMessage, extractNextRequiredField} from './utils';
import {errorDuplicateGuest, errorNotFound, FORM_NAMES} from './constants';
import styles from './styles.module.css';
import {useModalControls} from '@chekin/guestapp-ui-kit';
import {SDK_MODE} from '@chekin/core';

function FindReservationsByNameView() {
  const {t} = useTranslation('identity-verification');
  const {objectId} = useChekinSDK();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const {
    register,
    handleSubmit,
    formState: {errors},
    control,
    watch,
  } = useForm<FormTypes>();

  const {
    isOpen: showIncompleteDataModal,
    openModal: openIncompleteModal,
    closeModal: closeIncompleteModal,
  } = useModalControls();
  const {
    isOpen: showNotFoundModal,
    openModal: openNotFoundModal,
    closeModal: closeNotFoundModal,
  } = useModalControls();

  const [nextField, setNextField] = useState<NEXT_REQUIRED_FIELDS | null>(null);
  const idNumber = watch(FORM_NAMES.idNumber);
  const dateOfBirth = watch(FORM_NAMES.dateOfBirth);
  const checkoutDate = watch(FORM_NAMES.checkoutDate);

  const handleRetry = useCallback(() => {
    closeNotFoundModal();
  }, [closeNotFoundModal]);

  const handleBackToStart = useCallback(() => {
    closeNotFoundModal();
    setNextField(null);
    navigate(getFullPath(PATHS.verifyOnHousing), {replace: true});
  }, [closeNotFoundModal, getFullPath, navigate]);

  const {mutate: searchReservation, isPending: isLoading} = useMutation({
    mutationFn: async (data: FormTypes) => {
      if (!objectId) {
        throw new Error(t('housing_information_missing'));
      }

      return API.guest.searchForIV({
        housing_id: objectId,
        ...data,
      });
    },
    onSuccess: response => {
      const isQRIVOnPropertyMode =
        ChekinProSettings.isSDK && ChekinProSettings.mode === SDK_MODE.qrIVOnProperty;

      if (isQRIVOnPropertyMode && response?.reservation_id) {
        ChekinProSettings.events.onReservationFoundFromHousing({
          id: response.reservation_id,
          guestId: response.guest_id,
        });
        return;
      }

      if (!ChekinProSettings.isSDK && response?.guest_iv_link) {
        const url = new URL(response.guest_iv_link);
        const link = import.meta.env.DEV
          ? `http://localhost:3001${url.pathname + url.search}`
          : response.guest_iv_link;

        window.location.href = `${link}?splash=true&scanned=true`;

        return;
      }

      openNotFoundModal();
    },
    onError: (error: unknown) => {
      const errorMessage = extractErrorMessage(error);

      if (errorMessage === errorDuplicateGuest) {
        const field = extractNextRequiredField(error) ?? FORM_NAMES.idNumber;
        setNextField(field);
        openIncompleteModal();
      } else if (errorMessage === errorNotFound) {
        openNotFoundModal();
      } else {
        openNotFoundModal();
      }
    },
  });

  const onSubmit = useCallback(
    (data: FormTypes) => {
      searchReservation(data);
    },
    [searchReservation],
  );

  return (
    <div className={styles.findReservations_main}>
      <div className={styles.findReservations_content}>
        <div className={styles.findReservations_headingGroup}>
          <h1 className={styles.findReservations_heading}>
            {t('find_your_reservation')}
          </h1>
          <p className={styles.findReservations_description}>
            {t('enter_guest_information_to_search')}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className={styles.findReservations_form}>
          <div className={styles.findReservations_formField}>
            <Input
              {...register(FORM_NAMES.name, {
                required: t('required'),
              })}
              label={t('first_name')}
              control={control}
              error={errors[FORM_NAMES.name]?.message}
              autoCorrect="off"
              spellCheck={false}
            />
          </div>

          <div className={styles.findReservations_formField}>
            <Input
              {...register(FORM_NAMES.surname, {
                required: t('required'),
              })}
              label={t('last_name')}
              control={control}
              error={errors[FORM_NAMES.surname]?.message}
              autoCorrect="off"
              spellCheck={false}
            />
          </div>

          {(idNumber || nextField === FORM_NAMES.idNumber) && (
            <div className={styles.findReservations_formField}>
              <Input
                {...register(FORM_NAMES.idNumber)}
                label={t('document_number')}
                control={control}
                error={errors[FORM_NAMES.idNumber]?.message}
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
          )}

          {(dateOfBirth || nextField === FORM_NAMES.dateOfBirth) && (
            <div className={styles.findReservations_formField}>
              <Controller
                name={FORM_NAMES.dateOfBirth}
                control={control}
                render={({field}) => (
                  <Datepicker
                    label={t('birth_date')}
                    value={field.value ? dayjs(field.value).toDate() : undefined}
                    onChange={date =>
                      field.onChange(date ? dayjs(date as Date).format('YYYY-MM-DD') : '')
                    }
                    error={errors[FORM_NAMES.dateOfBirth]?.message}
                  />
                )}
              />
            </div>
          )}

          {(checkoutDate || nextField === FORM_NAMES.checkoutDate) && (
            <div className={styles.findReservations_formField}>
              <Controller
                name={FORM_NAMES.checkoutDate}
                control={control}
                render={({field}) => (
                  <Datepicker
                    label={t('check_out_date')}
                    value={field.value ? dayjs(field.value).toDate() : undefined}
                    onChange={date =>
                      field.onChange(date ? dayjs(date as Date).format('YYYY-MM-DD') : '')
                    }
                    error={errors[FORM_NAMES.checkoutDate]?.message}
                  />
                )}
              />
            </div>
          )}

          <div className={styles.findReservations_actions}>
            <Button
              label={isLoading ? <Loader /> : t('find_reservation_button')}
              type="submit"
              disabled={isLoading}
              loading={isLoading}
              className={styles.findReservations_searchButton}
            />
          </div>
        </form>
      </div>

      <IncompleteDataModal
        open={showIncompleteDataModal}
        onClose={closeIncompleteModal}
      />
      <NotFoundReservationModal
        open={showNotFoundModal}
        onRetry={handleRetry}
        onBackToStart={handleBackToStart}
      />
    </div>
  );
}

function Loader() {
  const {t} = useTranslation();
  return (
    <div className={styles.findReservations_searchingState}>
      <Loader2 size={20} className="animate-spin" />
      <div className={styles.findReservations_searchingMessage}>
        {t('searching') + '...'}
      </div>
    </div>
  );
}

export {FindReservationsByNameView};
